// Day and time range helpers for the schedule editor slot tools (Generate slots,
// Bulk update slots, Clear slots in range, Copy day).
// Day index is Monday 0 through Sunday 6, matching applicableDays and the
// Monday first schedule calendar.
export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export function dayIndex(date) {
  const day = new Date(date).getDay()
  return day === 0 ? 6 : day - 1
}

// Shifts by calendar days, keeping the wall clock time.
export function shiftByDays(date, days) {
  const shifted = new Date(date)
  shifted.setDate(shifted.getDate() + days)
  return shifted
}

// [4, 5] -> "Friday and Saturday"; [0, 2, 4] -> "Monday, Wednesday and Friday"
export function formatDayList(indices) {
  const names = [...indices].sort((a, b) => a - b).map(i => DAY_NAMES[i])
  if (names.length <= 1) return names.join('')
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
}

function priorityOf(slot) {
  return typeof slot.priority === 'number' ? slot.priority : 9999
}

// sources: [{ key, start, end, priority }] for the slots on the source day.
// Returns copies in priority order per target day, and how many were skipped
// because they would end at or after weekEnd (the exclusive end of the shown week);
// see placeInWeek for why a slot ending exactly at weekEnd cannot be stored.
export function buildDayCopies(sources, sourceIndex, targetIndices, weekEnd) {
  const ordered = [...sources].sort((a, b) => priorityOf(a) - priorityOf(b))
  const targets = [...targetIndices].filter(i => i !== sourceIndex).sort((a, b) => a - b)
  const limit = new Date(weekEnd).getTime()
  const copies = []
  let skipped = 0

  for (const targetIndex of targets) {
    const delta = targetIndex - sourceIndex
    for (const source of ordered) {
      const start = shiftByDays(source.start, delta)
      const end = shiftByDays(source.end, delta)
      if (end.getTime() >= limit) {
        skipped++
        continue
      }
      copies.push({ key: source.key, targetIndex, start, end })
    }
  }

  return { copies, skipped }
}

// Minutes since midnight; only the wall clock of a picker or slot Date matters.
export function minutesOfDay(date) {
  const d = new Date(date)
  return d.getHours() * 60 + d.getMinutes()
}

// A range whose end is earlier than its start runs past midnight into the next day.
export function crossesMidnight(from, to) {
  return minutesOfDay(to) < minutesOfDay(from)
}

// True when the slot starts inside the range on a checked day, both ends included.
// Past midnight the early hours belong to the night before: with Friday checked,
// 10 PM to 2 AM matches a slot starting Saturday at 1 AM. Sunday night reaches the
// Monday early hours, since the template week repeats.
export function slotStartsInRange(slotStart, dayIndices, from, to) {
  const day = dayIndex(slotStart)
  const minute = minutesOfDay(slotStart)
  const fromMinute = minutesOfDay(from)
  const toMinute = minutesOfDay(to)
  if (toMinute >= fromMinute) {
    return dayIndices.includes(day) && minute >= fromMinute && minute <= toMinute
  }
  if (minute >= fromMinute) return dayIndices.includes(day)
  if (minute <= toMinute) return dayIndices.includes((day + 6) % 7)
  return false
}

// Places a generated slot in the shown week. A slot starting on or after weekEnd (the
// exclusive end of the week) wraps to the same time a week earlier, the template's
// Monday. A slot that reaches weekEnd from Sunday is null: saved with end_day Monday, the
// editor reloads its end on the template's Monday, before its start.
export function placeInWeek(start, end, weekEnd) {
  const limit = new Date(weekEnd).getTime()
  if (new Date(start).getTime() >= limit) {
    return { start: shiftByDays(start, -7), end: shiftByDays(end, -7) }
  }
  if (new Date(end).getTime() >= limit) return null
  return { start: new Date(start), end: new Date(end) }
}

// "between 6:00 PM and 10:00 PM on Friday and Saturday"; labels are already formatted.
export function formatRangeScope(fromLabel, toLabel, indices) {
  return 'between ' + fromLabel + ' and ' + toLabel + ' on ' + formatDayList(indices)
}
